/**
 * VariableType → ComparisonOperator Mapping
 *
 * Single source of truth for which operators are valid per variable type.
 * V2 adds 4 new operators vs V1: containsAll, containsAny, exists, notExists.
 *
 * Used by:
 * - v2-condition-editor (UI) — which operators to show
 * - v2-variable-utils (services) — operator validation
 *
 * @module v2-variable-utils
 */

import type { ComparisonOperator, VariableType } from '@segmently/schema-models/v2';
import type { V2OperatorInfo } from '../types/variable-utils.types';

// ============================================================================
// VARIABLE TYPE → OPERATORS MAPPING
// ============================================================================

/**
 * VariableType → valid ComparisonOperator[] mapping.
 *
 * Derived from VariableType schema doc comment (openapi-v2.yaml).
 */
export const VARIABLE_TYPE_TO_OPERATORS: Readonly<Record<VariableType, readonly ComparisonOperator[]>> = {
  'string':   ['eq', 'neq', 'in', 'notIn', 'exists', 'notExists'],
  'number':   ['eq', 'neq', 'lt', 'gt', 'lte', 'gte', 'in', 'notIn', 'exists', 'notExists'],
  'boolean':  ['eq', 'neq', 'exists', 'notExists'],
  'date':     ['eq', 'neq', 'lt', 'gt', 'lte', 'gte', 'exists', 'notExists'],
  'enum':     ['eq', 'neq', 'in', 'notIn', 'exists', 'notExists'],
  'enum[]':   ['eq', 'neq', 'in', 'notIn', 'containsAll', 'containsAny', 'exists', 'notExists'],
} as const;

// ============================================================================
// OPERATOR UI METADATA
// ============================================================================

/**
 * All V2 ComparisonOperator UI metadata.
 */
export const V2_OPERATOR_INFO: Readonly<Record<ComparisonOperator, V2OperatorInfo>> = {
  lt: {
    operator: 'lt',
    label: '<',
    description: 'Less than',
    requiresValue: true,
    requiresMultiValue: false,
  },
  gt: {
    operator: 'gt',
    label: '>',
    description: 'Greater than',
    requiresValue: true,
    requiresMultiValue: false,
  },
  lte: {
    operator: 'lte',
    label: '<=',
    description: 'Less than or equal',
    requiresValue: true,
    requiresMultiValue: false,
  },
  gte: {
    operator: 'gte',
    label: '>=',
    description: 'Greater than or equal',
    requiresValue: true,
    requiresMultiValue: false,
  },
  eq: {
    operator: 'eq',
    label: '==',
    description: 'Equals',
    requiresValue: true,
    requiresMultiValue: false,
  },
  neq: {
    operator: 'neq',
    label: '!=',
    description: 'Not equals',
    requiresValue: true,
    requiresMultiValue: false,
  },
  in: {
    operator: 'in',
    label: 'in',
    description: 'Value is in list',
    requiresValue: true,
    requiresMultiValue: true,
  },
  notIn: {
    operator: 'notIn',
    label: 'not in',
    description: 'Value is not in list',
    requiresValue: true,
    requiresMultiValue: true,
  },
  containsAll: {
    operator: 'containsAll',
    label: 'contains all',
    description: 'Array contains all specified values',
    requiresValue: true,
    requiresMultiValue: true,
  },
  containsAny: {
    operator: 'containsAny',
    label: 'contains any',
    description: 'Array contains at least one specified value',
    requiresValue: true,
    requiresMultiValue: true,
  },
  exists: {
    operator: 'exists',
    label: 'exists',
    description: 'Variable has been set (not undefined)',
    requiresValue: false,
    requiresMultiValue: false,
  },
  notExists: {
    operator: 'notExists',
    label: 'not set',
    description: 'Variable has not been set (undefined)',
    requiresValue: false,
    requiresMultiValue: false,
  },
} as const;

// ============================================================================
// CONVENIENCE CONSTANTS
// ============================================================================

/**
 * Operators that do not require a comparison value.
 * The RouteGuard.value field is ignored for these operators.
 */
export const VALUELESS_OPERATORS: readonly ComparisonOperator[] = [
  'exists',
  'notExists',
] as const;

/**
 * Operators that require multiple values (comma-separated in RouteGuard.value).
 */
export const MULTI_VALUE_OPERATORS: readonly ComparisonOperator[] = [
  'in',
  'notIn',
  'containsAll',
  'containsAny',
] as const;
